import * as React from 'react';
import BaseStore from '@src/utils/store/base-store';
import useModal from '@src/utils/hooks/use-modal';
import useMount from '@src/utils/hooks/use-mount';
import Toast from '@src/components/toast';
import copy from '@src/components/copy';
import TakePhoto from '@src/components/take-photo';
import Services from '@src/services/general';
import formatRoute from '@src/components/route/format-route';
import { POSTER_CANVAS_SIZE, createPosterMembers } from '@src/ui-components/mo-poster';
import { computed, observable, untracked } from 'mobx';
import { autobind } from 'core-decorators';
import ShareCopyFooter from './components/share-copy-footer';
import TagEditorModal from './components/tag-editor-modal';
import TagEditorModalStore from './components/tag-editor-modal/store';
import RoleEditorModal from './components/role-editor-modal';
import RoleEditorModalStore from './components/role-editor-modal/store';
import routeConfigs from '../app-router/route-configs';
import rootStore from '@src/mobx-store/root-store';
import $$tracker from '@src/tracker';

/** 展示字段空值统一占位（viewmodel-first：兜底收敛在 Store 层） */
const EMPTY_DISPLAY_TEXT = '--';

/** 动态海报独立路由片段（routeConfigs.ENDED_POSTER），活动主页基地按此派生 */
const POSTER_ROUTE_SEGMENT = '/ended-poster';

const POSTER_QR_ENTRY_SOURCE = 'poster_qr';
const INVITE_LINK_ENTRY_SOURCE = 'invite_link';

/** 门派/性别确认值来源为空时的页面侧本地缺省（findings 2026-09-04 用户决策：不持久化、不调保存接口） */
const DEFAULT_SECT = 'yishui';
const DEFAULT_GENDER = 'male';

/** 五门派中文枚举 → sectKey 前缀（与 mo-poster SECT_GROUP_NUMBERS 键一致的 5×2 映射） */
const MENPAI_NAME_TO_SECT = {
  天王: 'tianwang',
  千机: 'qianji',
  烬宗: 'jinzhong',
  易水: 'yishui',
  武当: 'wudang'
};

/** sectKey 前缀 → 海报门派展示名（初始映射自 get_poster.role_menpai_name，编辑后跟随确认值） */
const SECT_TO_MENPAI_NAME = {
  tianwang: '天王',
  qianji: '千机',
  jinzhong: '烬宗',
  yishui: '易水',
  wudang: '武当'
};

/** get_poster.role_gender 中文值 → gender 键 */
const ROLE_GENDER_TO_GENDER = {
  男: 'male',
  女: 'female'
};

/** gender 键 → 海报性别展示文本 */
const GENDER_TO_ROLE_TEXT = {
  male: '男',
  female: '女'
};

const TAG_DATA_ABNORMAL_MESSAGE = '标签数据异常，请稍后重试';

const POSTER_LOAD_FAILED_MESSAGE = '加载失败，请稍后重试';

/* 主操作与奖励反馈文案：「海报已保存」「海报生成失败，请稍后重试」取自 PRD 失败提示文案表；
   复制成功/失败与 QR 目标缺失文案 PRD 未定义，按页面既有文案风格补充 */
const QR_TARGET_UNAVAILABLE_MESSAGE = '二维码数据缺失，请稍后重试';
const COPY_SUCCESS_MESSAGE = '复制链接成功，请粘贴分享';
const COPY_FAILED_MESSAGE = '复制失败，请稍后重试';
const POSTER_SAVED_MESSAGE = '名帖已生成';
/* 活动中保存成功 Toast 合并背包领奖引导（2026-09-21 需求：首份奖励无独立获奖提示）：
   引导式表述不承诺发放结果（claim 失败场景由独立的失败 Toast 兜底），
   单条 Toast 合并规避 antd-mobile Toast 单例互相覆盖 */
const POSTER_SAVED_WITH_PRIZE_GUIDE_MESSAGE = '名帖已生成，请前往“我的背包”领奖';
const POSTER_GENERATE_FAILED_MESSAGE = '海报生成失败，请稍后重试';
const SHARE_PRIZE_FAILED_MESSAGE = '奖励领取失败，请稍后重试';

/* 来源感知关闭（Task 7）：调用方（TEAM/MAIN/GUEST）跳转本页时经 URL query 显式携带来源 route key，
   本页右上角关闭按该来源回退；redirect 只支持裸 route key，query 由调用方以 formatRoute 拼装 */
const CLOSE_SOURCE_QUERY_PARAM = 'source';

/** 来源 route key 白名单（安全校验，防任意跳转）：只接受实际存在跳转关系的四个受控入口页（VETERAN_REVIEW 为总结页海报回流） */
const SAFE_CLOSE_SOURCE_ROUTE_KEYS = ['TEAM', 'MAIN', 'GUEST', 'VETERAN_REVIEW'];

/** GUEST return flow only inherits controlled invite context; arbitrary query fields are discarded. */
const SAFE_GUEST_CLOSE_QUERY_PARAMS = ['entry_source', 'from_team_id', 'share_code'];

/** 无来源或来源非法时的统一回退目标（findings 用户确认-1：回组队主页） */
const CLOSE_FALLBACK_ROUTE_KEY = 'TEAM';

/**
 * 展示文本空值兜底：非空字符串原样输出，其余统一 '--'。
 *
 * @param {string} value 接口返回的展示字段原始值。
 * @returns {string} 可直接渲染的展示文本。
 */
function formatDisplayText(value) {
  return typeof value === 'string' && value ? value : EMPTY_DISPLAY_TEXT;
}

/**
 * 剑侠评分展示格式：数字（或纯文本值）补 '分' 后缀，对齐 PSD 占位形态（'8999分'）。
 *
 * @param {number|string} value get_poster.jianxia_score 原始值（后端允许暂空）。
 * @returns {string} 老玩家评分展示文本，空值为 '--'。
 */
function formatJianxiaScoreText(value) {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return `${value}分`;
  }

  if (typeof value === 'string' && value) {
    return value.slice(-1) === '分' ? value : `${value}分`;
  }

  return EMPTY_DISPLAY_TEXT;
}

/**
 * 注册日期展示格式：'YYYY-MM-DD' 转为 PSD 基线形态（'2026年8月1日'）；无法解析的非空值原样展示。
 *
 * @param {string} value get_poster.register_date 原始值。
 * @returns {string} 注册日期展示文本，空值为 '--'。
 */
function formatRegisterDateText(value) {
  if (typeof value !== 'string' || !value) {
    return EMPTY_DISPLAY_TEXT;
  }

  const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) {
    return `${Number(match[1])}年${Number(match[2])}月${Number(match[3])}日`;
  }

  return value;
}

/**
 * 分组内标签项 → 标签名：2026-09-21 实测 tag_list 组内为 { name, desc } 对象
 * （desc 为达成条件描述，海报页展示预留，见 createTagDescMap）；兼容旧字符串形态；
 * 非法项返回空串由调用方过滤。
 *
 * @param {string|object} item 分组内单个标签项。
 * @returns {string} 标签名；不可解析时为空串。
 */
function pickTagName(item) {
  if (typeof item === 'string') {
    return item;
  }

  if (item && typeof item === 'object' && typeof item.name === 'string') {
    return item.name;
  }

  return '';
}

/**
 * 归一化标签列表：tag_list 为按稀有度分组的对象（键 "3"/"2"/"1"，3 为最高稀有度，
 * 组内为 { name, desc } 对象——2026-09-21 实测），按 3→2→1 摊平为候选池（组内保持接口顺序）
 * ——默认取前三个即高稀有度优先；兼容旧字符串数组形态（顺序原样，弹窗确认值回传同形态）。
 * 非空串过滤与异常池判定语义不变。
 *
 * @param {object|string[]|undefined} rawTagList get_poster.tag_list 原始值。
 * @returns {string[]} 干净的降序候选池数组。
 */
function normalizeTagList(rawTagList) {
  if (Array.isArray(rawTagList)) {
    return rawTagList.filter(tag => typeof tag === 'string' && tag);
  }

  if (rawTagList && typeof rawTagList === 'object') {
    return ['3', '2', '1'].reduce(
      (list, rarity) => list.concat(Array.isArray(rawTagList[rarity]) ? rawTagList[rarity].map(pickTagName).filter(tag => tag) : []),
      []
    );
  }

  return [];
}

/**
 * 构建 name → 稀有度映射（标签弹窗选中态边框取色依据）：仅分组对象形态可解析出稀有度
 * （组内项经 pickTagName 取名，兼容字符串项），旧数组形态与缺失项由消费侧兜底稀有度 1。
 *
 * @param {object|string[]|undefined} rawTagList get_poster.tag_list 原始值。
 * @returns {Object<string, number>} 标签名到稀有度（1/2/3）的映射。
 */
function createTagRarityMap(rawTagList) {
  const rarityMap = {};
  if (rawTagList && typeof rawTagList === 'object' && !Array.isArray(rawTagList)) {
    Object.keys(rawTagList).forEach(rarity => {
      const parsedRarity = Number(rarity);
      if (Array.isArray(rawTagList[rarity]) && (parsedRarity === 1 || parsedRarity === 2 || parsedRarity === 3)) {
        rawTagList[rarity].forEach(item => {
          const tag = pickTagName(item);
          if (tag) {
            rarityMap[tag] = parsedRarity;
          }
        });
      }
    });
  }
  return rarityMap;
}

/**
 * 构建 name → desc 映射（2026-09-21 新增字段的展示数据准备）：desc 为标签达成条件描述，
 * 仅分组内 { name, desc } 对象项入表；desc 展示位待设计稿到位后接入（届时消费 store.tagDescMap）。
 *
 * @param {object|undefined} rawTagList get_poster.tag_list 原始值。
 * @returns {Object<string, string>} 标签名到达成条件描述的映射。
 */
function createTagDescMap(rawTagList) {
  const descMap = {};
  if (rawTagList && typeof rawTagList === 'object' && !Array.isArray(rawTagList)) {
    Object.keys(rawTagList).forEach(rarity => {
      if (!Array.isArray(rawTagList[rarity])) {
        return;
      }

      rawTagList[rarity].forEach(item => {
        if (item && typeof item === 'object' && typeof item.name === 'string' && item.name && typeof item.desc === 'string') {
          descMap[item.name] = item.desc;
        }
      });
    });
  }
  return descMap;
}

/**
 * 从当前运行地址派生活动主页 URL：
 * 仅移除结尾的 /ended-poster 路由片段，不硬编码任何环境域名；
 * 同时丢弃进入海报页时携带的 query/hash，避免把上一位分享者的参数带进新二维码。
 *
 * @param {string} locationHref 当前运行地址（window.location.href）。
 * @returns {string} 活动主页基地 URL；地址不可解析时返回空串。
 */
function createActivityHomeUrl(locationHref) {
  if (typeof locationHref !== 'string' || !locationHref) {
    return '';
  }

  let url;
  try {
    url = new URL(locationHref);
  } catch (error) {
    return '';
  }

  // HashRouter 下路由段（/ended-poster）位于 hash 内，物理 pathname 本身就是活动主页路径，
  // 下面的 pathname 剥离实为无操作但结果恰好正确；为 history 路由形态兼容保留该剥离（勿删，勿据此改用 hash 解析）
  url.pathname = url.pathname.replace(/\/ended-poster\/?$/, '/');
  url.search = '';
  url.hash = '';
  return url.href;
}

/**
 * 以现有路由格式化工具在活动部署入口上构造 Guest HashRouter URL；
 * team_id / share_code 缺一时不产出 team 目标，避免生成无法完成入队的二维码。
 *
 * @param {string} baseUrl 活动主页基地 URL。
 * @param {{team_id:string, share_code:string}} inviteParams get_poster.invite_params。
 * @param {'poster_qr'|'invite_link'} entrySource 分享载体来源。
 * @returns {string} 完整 Guest 邀请目标 URL；参数缺失或基地不可解析时返回空串。
 */
function withTeamShareParams(baseUrl, inviteParams, entrySource) {
  if (!inviteParams.team_id || !inviteParams.share_code) {
    return '';
  }

  let url;
  try {
    url = new URL(baseUrl);
  } catch (error) {
    return '';
  }

  url.search = '';
  url.hash = formatRoute(routeConfigs.GUEST.path, {
    entry_source: entrySource,
    from_team_id: inviteParams.team_id,
    share_code: inviteParams.share_code
  });
  return url.href;
}

/**
 * 构造不含邀请上下文的活动主页显式 HashRouter URL。
 *
 * @param {string} baseUrl 运行时派生的活动部署地址。
 * @param {'poster_qr'|'invite_link'} entrySource 分享载体来源。
 * @returns {string} 活动主页 HashRouter URL；不可解析时返回空串。
 */
function createActivityHomeHashUrl(baseUrl, entrySource) {
  let url;
  try {
    url = new URL(baseUrl);
  } catch (error) {
    return '';
  }

  url.search = '';
  url.hash = formatRoute(routeConfigs.MAIN.path, { entry_source: entrySource });
  return url.href;
}

/**
 * 初始门派确认值：映射自 get_poster.role_menpai_name。
 * menpai_id 与 PSD 门派组顺序不一致（mock 中武当为 menpai_id=3、组5），故不参与视觉映射。
 *
 * @param {object} posterData get_poster 返回数据。
 * @returns {string} sectKey 门派前缀；来源为空或不可映射时回退本地默认天王。
 */
function resolveInitialSect(posterData) {
  const sect = posterData && MENPAI_NAME_TO_SECT[posterData.role_menpai_name];
  return sect || DEFAULT_SECT;
}

/**
 * 初始性别确认值：映射自 get_poster.role_gender。
 *
 * @param {object} posterData get_poster 返回数据。
 * @returns {string} gender 键；来源为空或不可映射时回退本地默认男。
 */
function resolveInitialGender(posterData) {
  const gender = posterData && ROLE_GENDER_TO_GENDER[posterData.role_gender];
  return gender || DEFAULT_GENDER;
}

/**
 * 服务异常的可展示文案：优先取异常 message（zt 服务失败时为 ServiceResponseError 携带的服务端文案），
 * 缺失时回退调用方给定的场景兜底文案。
 *
 * @param {Error|object} error 服务层抛出的异常。
 * @param {string} [fallbackMessage] 场景兜底文案（默认为页面加载失败文案）。
 * @returns {string} 可直接进入 Toast 的文案。
 */
function getErrorMessage(error, fallbackMessage = POSTER_LOAD_FAILED_MESSAGE) {
  if (error && typeof error.message === 'string' && error.message) {
    return error.message;
  }

  return fallbackMessage;
}

/**
 * 从路由 query 解析来源 route key 并做白名单校验：
 * 本应用为 HashRouter（app-router），跳转 query 落在 hash 段（如 /#/ended-poster?source=TEAM），
 * 物理 window.location.search 恒为空，来源必须经路由 history 的 location.search 读取
 * （BaseStore.use 已通过 useHistory() 捕获 $history，super.use 之后调用即就位）；
 * 以 URLSearchParams 解析，不依赖 new URL 构造器（规避旧浏览器无 polyfill 的静默风险）。
 * 只接受显式传入且属于安全白名单的 route key；缺失、非法或 history 不可读一律返回空串，
 * 由 closePage 统一回退 TEAM。
 *
 * @param {object} history 路由 history 实例（$history）。
 * @returns {string} 已校验的来源 route key；无合法来源时为空串。
 */
function resolveCloseSourceRouteKey(history) {
  const search = history && history.location && typeof history.location.search === 'string' ? history.location.search : '';
  const source = new URLSearchParams(search).get(CLOSE_SOURCE_QUERY_PARAM);
  return SAFE_CLOSE_SOURCE_ROUTE_KEYS.indexOf(source) === -1 ? '' : source;
}

/**
 * 只快照允许从海报页回流到 GUEST 的邀请字段。
 * `return_url` 与其余路由 query 刻意忽略，防止任意跳转。
 *
 * @param {object} history 含 HashRouter search 段的路由 history。
 * @returns {object} 可直接交给 formatRoute 编码的三个受控值。
 */
function resolveGuestCloseQueryParams(history) {
  const search = history && history.location && typeof history.location.search === 'string' ? history.location.search : '';
  const searchParams = new URLSearchParams(search);
  return SAFE_GUEST_CLOSE_QUERY_PARAMS.reduce(
    (params, key) => ({
      ...params,
      [key]: searchParams.get(key) || ''
    }),
    {}
  );
}

/**
 * 动态海报页根 owner：聚合共享 activity session 与 get_poster，
 * 输出海报 ViewModel、QR 三态目标与两个编辑弹窗的打开编排。
 * 确认值（标签顺序、门派/性别）归本 Store；草稿与 1~3 约束归各弹窗 Store，
 * 协作一律通过构造注入的 getter / onConfirm 回调完成，不传递 pageStore 引用。
 */
class EndedPosterStore extends BaseStore {
  @observable loadStatus = 'idle';

  /** 活动阶段事实，仅来自共享 activity session（不从 get_poster 空字段反推） */
  @observable activityStage = 'unknown';

  /** 玩家身份事实，仅来自共享 activity session */
  @observable playerType = 'unknown';

  /** get_poster 聚合数据（原样保存，展示映射统一走 computed ViewModel） */
  @observable.ref posterData = null;

  /* 当前 team/home 目标的短链（createTinyUrl 生成）；空串表示未就绪或失败，currentQrTarget 回退长链形态 */
  @observable tinyUrl = '';

  // 短链生成单飞锁 + 一次性标记：防并发重入与失败后的串行重试（白名单拒绝重试必然再失败，长链回退已可用）
  tinyUrlRequest = null;
  tinyUrlAttempted = false;

  /** 已确认标签，按确认顺序排列；get_poster 加载后默认取降序候选池的前 3 个（高稀有度优先） */
  @observable confirmedTags = [];

  /** 标签名 → 稀有度映射（1/2/3）；分组 tag_list 解析产物，旧数组形态为空对象由 getter 兜底 */
  tagRarityMap = {};

  /** 标签名 → 达成条件描述映射（2026-09-21 新增 desc 字段的展示数据准备）；展示位待设计稿，暂不接 ViewModel */
  tagDescMap = {};

  /** 已确认门派键（5×2 映射前缀） */
  @observable confirmedSect = DEFAULT_SECT;

  /** 已确认性别键（5×2 映射后缀） */
  @observable confirmedGender = DEFAULT_GENDER;

  // 初始化跨 session 与 get_poster 两个请求，单独加锁防止并发重复初始化
  initializePromise = null;

  pageExposureReported = false;

  // 复制与保存是两个独立主操作（状态-6-4 海报生成中），各自互斥、成功/失败即解锁；
  // 奖励链路不占用锁（与主操作解耦），失败重试仅依赖这两个标记
  isCopying = false;
  isSavingPoster = false;

  /** 已校验的关闭来源 route key（use() 时从 URL query 接收并白名单校验后存储）；空串表示无合法来源 */
  closeSourceRouteKey = '';

  /** Controlled GUEST invite context captured once by use(); other sources never retain route query. */
  guestCloseQueryParams = null;

  /**
   * @param {object} options 页面依赖（由根 Provider 或聚焦测试注入）。
   * @param {object} options.activitySessionStore 共享活动事实 owner。
   * @param {object} [options.teamFactStore] 用户级队伍事实快照（小程序分享 reaction 数据源，默认 root 单例）。
   * @param {object} [options.ztService] 可替换的服务实例（默认取 BaseStore 的 $ztService）。
   * @param {Function} [options.createTinyUrlRequest] 可替换的短链生成 provider（默认 general Services.createTinyUrl）。
   */
  constructor({ activitySessionStore, teamFactStore = rootStore.teamFactStore, ztService, createTinyUrlRequest = Services.createTinyUrl } = {}) {
    super();
    this.activitySessionStore = activitySessionStore;
    this.teamFactStore = teamFactStore;
    if (ztService) {
      this.$ztService = ztService;
    }
    this.createTinyUrlRequest = createTinyUrlRequest;
    // 弹窗 Store 只拿 getter / onConfirm 回调，不拿 pageStore 引用（store-autonomy）
    this.tagEditorModalStore = new TagEditorModalStore({
      getCandidateTags: this.getCandidateTagList,
      getConfirmedTags: this.getConfirmedTagList,
      getTagRarity: this.getTagRarity,
      getTagDesc: this.getTagDesc,
      onConfirm: this.applyTagConfirm
    });
    this.roleEditorModalStore = new RoleEditorModalStore({
      getSect: this.getCurrentSect,
      getGender: this.getCurrentGender,
      onConfirm: this.applyRoleConfirm
    });
  }

  /** 注册路由上下文、接收并校验存储关闭来源，挂载后启动一次页面初始化；两个弹窗经 useModal 协议注册。 */
  use(props) {
    super.use(props);
    // 来源接收（Task 7）：调用方跳转本页时经 source 查询参数显式携带来源 route key，
    // HashRouter 下 query 在 hash 段，经 super.use 已捕获的 $history.location.search 读取；
    // 在生命周期入口一次性解析并做白名单校验存储，closePage 只消费这里的合法值（findings：来源解析归页面根 owner）
    // untracked：use() 随每次渲染执行于 observer 跟踪上下文，而 $history 是 @observable 且每渲染被
    // super.use 重新赋值（深观测下产生新包装对象），被跟踪的一次性读取会形成「赋值→变更→重渲染」死循环，
    // 故来源解析必须脱离跟踪执行（useStore 每渲染调用 use()，生产同样会触发）
    this.closeSourceRouteKey = untracked(() => resolveCloseSourceRouteKey(this.$history));
    this.guestCloseQueryParams = this.closeSourceRouteKey === 'GUEST' ? untracked(() => resolveGuestCloseQueryParams(this.$history)) : null;
    this.tagEditorModal = useModal(TagEditorModal, { store: this.tagEditorModalStore });
    this.roleEditorModal = useModal(RoleEditorModal, { store: this.roleEditorModalStore });
    useMount(this.initialize);
  }

  /**
   * 归一化后的标签候选池。
   *
   * @returns {string[]} 非空字符串候选列表；缺失/为空/异常时为空数组。
   */
  @computed
  get tagList() {
    return normalizeTagList(this.posterData && this.posterData.tag_list);
  }

  /**
   * 标签池数据异常态：tag_list 缺失、为空或异常时为 true，
   * 供 UI Toast 与编辑/生成操作阻断共用（findings 边界-6-4）。
   *
   * @returns {boolean} 是否处于异常标签池状态。
   */
  @computed
  get isTagDataAbnormal() {
    return this.tagList.length === 0;
  }

  /** get_poster.invite_params 的安全读取视图。 */
  @computed
  get inviteParams() {
    const posterData = this.posterData;
    return (posterData && posterData.invite_params) || {};
  }

  /** team 模式要求 team_id 与 share_code 同时有效（findings：缺关键参数时阻断 team 模式）。 */
  @computed
  get hasTeamInviteParams() {
    const inviteParams = this.inviteParams;
    return !!(inviteParams.team_id && inviteParams.share_code);
  }

  /**
   * QR 三态互斥键（mo-poster qrMode prop）：
   * 结束态固定 download；活动中 team_id 与 share_code 均有效为 team，否则 home。
   *
   * @returns {'team'|'home'|'download'} 当前二维码互斥态。
   */
  @computed
  get qrMode() {
    if (this.activityStage === 'ended') {
      return 'download';
    }

    return this.hasTeamInviteParams ? 'team' : 'home';
  }

  /** Maps the UI QR mode to the CSV poster kinds enum. */
  get trackerPosterKind() {
    return { team: 'team', home: 'activity_home', download: 'game_download' }[this.qrMode];
  }

  /** Uses from_team_id/team_id as the share attribution ID; non-Team posters have no req_id. */
  get trackerRequestId() {
    return this.inviteParams.team_id || '';
  }

  /**
   * 当前二维码目标 URL：
   * download 只用 get_poster.download_url（不回退活动主页、不合并参数）；
   * team 指向同部署 Guest HashRouter 路由并携带完整邀请上下文；home 只指向 HashRouter 首页。
   * team/home 的短链就绪后优先输出短链（约 27 字符 vs 长链 100+，QR 版本从 version 7 降到 version 2，
   * 74px 槽位下模块密度近乎翻倍）；未就绪或生成失败时回退长链形态。
   * 缺当前模式必需数据时输出空串，由 isCurrentQrTargetAvailable 标记不可操作。
   *
   * @returns {string} 二维码目标 URL；不可操作时为空串。
   */
  @computed
  get currentQrTarget() {
    if (this.qrMode === 'download') {
      const downloadUrl = this.posterData ? this.posterData.download_url : '';
      return typeof downloadUrl === 'string' && downloadUrl ? downloadUrl : '';
    }

    if (this.tinyUrl) {
      return this.tinyUrl;
    }

    const activityHomeUrl = createActivityHomeUrl(window.location.href);
    if (!activityHomeUrl) {
      return '';
    }

    if (this.qrMode === 'team') {
      return withTeamShareParams(activityHomeUrl, this.inviteParams, POSTER_QR_ENTRY_SOURCE);
    }

    return createActivityHomeHashUrl(activityHomeUrl, POSTER_QR_ENTRY_SOURCE);
  }

  /** Builds the copied-link variant with a distinct entry_source while retaining Team attribution fields. */
  @computed
  get currentCopyTarget() {
    if (this.qrMode === 'download') {
      return this.currentQrTarget;
    }
    const activityHomeUrl = createActivityHomeUrl(window.location.href);
    if (!activityHomeUrl) {
      return '';
    }
    return this.qrMode === 'team'
      ? withTeamShareParams(activityHomeUrl, this.inviteParams, INVITE_LINK_ENTRY_SOURCE)
      : createActivityHomeHashUrl(activityHomeUrl, INVITE_LINK_ENTRY_SOURCE);
  }

  /** 当前模式必需数据缺失（如结束态 download_url 为空）时的不可操作标记。 */
  @computed
  get isCurrentQrTargetAvailable() {
    return !!this.currentQrTarget;
  }

  /** 5×2 视觉映射键（mo-poster sectKey prop），由门派/性别确认值拼合。 */
  @computed
  get sectKey() {
    return `${this.confirmedSect}-${this.confirmedGender}`;
  }

  /**
   * 海报展示 ViewModel（viewmodel-first：空值兜底与格式化全部收敛在此）。
   * 新玩家不输出曾用名与剑侠评分展示位；标签按确认顺序以 ' / ' 连接。
   *
   * @returns {object} mo-poster viewModel prop 消费的稳定视图模型。
   */
  @computed
  get posterViewModel() {
    const posterData = this.posterData || {};
    const viewModel = {
      tagsText: this.confirmedTags.length ? this.confirmedTags.join(' / ') : EMPTY_DISPLAY_TEXT,
      menpaiName: SECT_TO_MENPAI_NAME[this.confirmedSect] || EMPTY_DISPLAY_TEXT,
      genderText: GENDER_TO_ROLE_TEXT[this.confirmedGender] || EMPTY_DISPLAY_TEXT,
      registerDateText: formatRegisterDateText(posterData.register_date)
    };

    // 曾用名与剑侠评分是老玩家回顾数据；新玩家身份只看 session，不输出这两个展示位
    if (this.playerType === 'veteran') {
      viewModel.formerRoleNameText = formatDisplayText(posterData.role_name);
      viewModel.jianxiaScoreText = formatJianxiaScoreText(posterData.jianxia_score);
    }

    return viewModel;
  }

  /**
   * 页面初始化：先等待共享 activity session 给出活动阶段与玩家身份，
   * 再加载 get_poster 聚合数据并落定初始门派/性别确认值。
   *
   * @returns {Promise<object|undefined>} 加载完成的 get_poster 数据；失败经 Toast 兜底后返回 undefined。
   */
  @autobind
  initialize() {
    if (this.initializePromise) {
      return this.initializePromise;
    }

    this.loadStatus = 'loading';
    const request = Promise.resolve()
      .then(() => this.activitySessionStore.ensureInitialized())
      .then(activityContext => {
        // 过期 session 请求按契约返回空：清空页面事实但不留错误反馈
        if (!activityContext) {
          this.activityStage = 'unknown';
          this.playerType = 'unknown';
          this.loadStatus = 'idle';
          return undefined;
        }

        this.activityStage = activityContext.activityStage;
        this.playerType = activityContext.playerType;

        return this.loadPoster();
      })
      .catch(error => {
        this.loadStatus = 'error';
        Toast.fail(getErrorMessage(error));
        return undefined;
      })
      .then(result => {
        // initializePromise 仅在本链路终点重置；链路执行期间并发的 initialize() 会被首行锁直接复用本 promise，
        // 不存在“过期请求”分支（原 !== request 守卫为不可达死代码，按 Task 6 review 跟进项删除）
        this.initializePromise = null;
        return result;
      });

    this.initializePromise = request;
    return request;
  }

  /**
   * 加载 get_poster 并落定初始确认值；就绪后为 team/home 目标异步生成一次短链。
   *
   * @returns {Promise<object|undefined>} get_poster 数据；异常向上交给 initialize 统一 Toast。
   */
  @autobind
  loadPoster() {
    return Promise.resolve()
      .then(() => this.$ztService.getPoster())
      .then(posterData => {
        this.posterData = posterData || {};
        // 稀有度映射随 posterData 一起落地（标签弹窗选中态边框取色依据）；
        // desc 映射同步落地（2026-09-21 新增字段展示预留，待设计稿接入展示位）
        this.tagRarityMap = createTagRarityMap(posterData && posterData.tag_list);
        this.tagDescMap = createTagDescMap(posterData && posterData.tag_list);
        // 标签默认值沿用接口顺序取前三个；门派/性别来源为空时本地默认天王+男，均不持久化
        this.confirmedTags = this.tagList.slice(0, 3);
        this.confirmedSect = resolveInitialSect(this.posterData);
        this.confirmedGender = resolveInitialGender(this.posterData);
        this.loadStatus = 'ready';
        if (!this.pageExposureReported) {
          $$tracker.pagePosterEditor(this.trackerPosterKind);
          this.pageExposureReported = true;
        }
        this.registerTeamFact();
        this.loadTinyUrl();
        return posterData;
      });
  }

  /**
   * 登记用户级队伍事实（get_poster.invite_params 视角）：全局分享 reaction 统一推送。
   * 沿用本页"team_id/share_code 缺一不产出客态目标"的契约（归因完整性优先），缺失按无队伍登记。
   *
   * @returns {void}
   */
  registerTeamFact() {
    this.teamFactStore.update({
      hasTeam: this.hasTeamInviteParams,
      teamId: this.inviteParams.team_id || ''
    });
  }

  /**
   * 为当前 team/home 长链目标异步生成一次短链（QR 渲染与复制链接共用 currentQrTarget，自动跟随切换）。
   * 短链是纯增强：任何失败（接口异常 / 白名单拒绝 / 返回缺 tiny_url）静默保持长链，
   * 不 Toast、不重试、不阻塞主操作；download 态与目标不可用时不发起。
   * 请求键校验防止页面数据变化后把过期短链落到新目标上。
   *
   * @returns {Promise<undefined>} 生成落地后 resolve；失败被吞，永不 reject。
   */
  @autobind
  loadTinyUrl() {
    if (this.tinyUrlRequest || this.tinyUrl || this.tinyUrlAttempted || this.qrMode === 'download') {
      return this.tinyUrlRequest || Promise.resolve(undefined);
    }

    const longTarget = this.currentQrTarget;
    if (!longTarget) {
      return Promise.resolve(undefined);
    }

    // 发起即标记：页面生命周期内只尝试一次，成败均不重试
    this.tinyUrlAttempted = true;

    let request;
    request = Promise.resolve()
      .then(() => this.createTinyUrlRequest(longTarget))
      .then(data => {
        const tinyUrl = data && typeof data.tiny_url === 'string' ? data.tiny_url : '';
        // 目标键校验：落库瞬间 currentQrTarget 仍是发起时的长链（posterData/session 未被重新加载改变）
        if (tinyUrl && !this.tinyUrl && this.currentQrTarget === longTarget) {
          this.tinyUrl = tinyUrl;
        }
        return undefined;
      })
      .catch(() => undefined)
      .then(result => {
        if (this.tinyUrlRequest === request) {
          this.tinyUrlRequest = null;
        }
        return result;
      });

    this.tinyUrlRequest = request;
    return request;
  }

  /**
   * 打开标签编辑弹窗：异常标签池先阻断并 Toast（供生成操作共用的数据异常态）；
   * 正常时先把候选池与当前确认值同步为弹窗草稿快照，再经 useModal 实例打开。
   *
   * @returns {undefined}
   */
  @autobind
  openTagEditor() {
    if (this.isTagDataAbnormal) {
      Toast.fail(TAG_DATA_ABNORMAL_MESSAGE);
      return undefined;
    }

    this.tagEditorModalStore.syncSnapshot();
    if (this.tagEditorModal) {
      this.tagEditorModal.open();
    }

    return undefined;
  }

  /**
   * 打开门派/性别编辑弹窗：先把当前确认值同步为弹窗草稿快照，再经 useModal 实例打开。
   * 门派/性别两类用户均可编辑（2026-09-22 回退老玩家锁定口径）。
   *
   * @returns {undefined}
   */
  @autobind
  openRoleEditor() {
    this.roleEditorModalStore.syncSnapshot();
    if (this.roleEditorModal) {
      this.roleEditorModal.open();
    }

    return undefined;
  }

  /**
   * 接收标签弹窗 onConfirm 回调：按确认顺序更新页面确认值并驱动海报 ViewModel。
   *
   * @param {string[]} tags 弹窗确认的有序标签（1~3 个）。
   * @returns {undefined}
   */
  @autobind
  applyTagConfirm(tags) {
    this.confirmedTags = normalizeTagList(tags).slice(0, 3);
    return undefined;
  }

  /**
   * 接收门派弹窗 onConfirm 回调：更新确认值切换 5×2 视觉映射；只作用于当前海报，不持久化。
   *
   * @param {string} sect 门派键（tianwang/qianji/jinzhong/yishui/wudang）。
   * @param {string} gender 性别键（male/female）。
   * @returns {undefined}
   */
  @autobind
  applyRoleConfirm(sect, gender) {
    if (!SECT_TO_MENPAI_NAME[sect] || !GENDER_TO_ROLE_TEXT[gender]) {
      return undefined;
    }

    this.confirmedSect = sect;
    this.confirmedGender = gender;
    return undefined;
  }

  /**
   * 「前往组队页」（底部左按钮，2026-09-20 需求：原「生成邀请链接」复制入口移入分享海报弹窗底部）。
   * 组队域门控口径与底栏/backpack 一致（activitySessionStore.reviewCleared）：
   * 回顾义务已清直达组队页；未清（含事实未知、活动结束）回活动首页由身份分流承接。
   *
   * @returns {undefined}
   */
  @autobind
  goTeamPage() {
    // 数据未就绪静默忽略（视图已用 actionsDisabled 禁用按钮，Store 侧防御性门控与 copyCurrentTarget 一致）
    if (this.loadStatus !== 'ready') {
      return undefined;
    }

    this.redirect(this.activitySessionStore.reviewCleared ? 'TEAM' : 'MAIN');
    return undefined;
  }

  /**
   * 「复制分享链接」（分享海报弹窗底部按钮，2026-09-20 需求：入口由底部左按钮移入弹窗）：
   * 复制当前 QR 模式的目标（team/home 邀请 URL 或 download 页），三个 QR 态共用该入口
   * （findings QR 与动作语义决策）。首分享奖励已随「保存海报」打开弹窗时申请（savePoster →
   * claimSharePrizeAfterSuccess），此处不再重复调用 claim_prize_share（2026-09-20 需求）。
   * 复制成功判定唯一依据为 copy 返回的 isSupport（findings / component-recall A3），成败各自 Toast；
   * copy 为同步 API，锁在本次调用内即时结算（防同帧重入）。
   *
   * 前置阻断（按序）：数据未就绪静默忽略（视图已用 actionsDisabled 禁用按钮，Store 侧按
   * store-autonomy 再做一层防御性门控——home/team 目标需运行地址与 invite_params 合并，未就绪时不完整，
   * 目标缺失时 Toast 阻断）。
   *
   * @returns {undefined}
   */
  @autobind
  copyCurrentTarget() {
    if (this.isCopying) {
      return undefined;
    }

    if (this.loadStatus !== 'ready') {
      return undefined;
    }

    if (!this.currentCopyTarget) {
      Toast.fail(QR_TARGET_UNAVAILABLE_MESSAGE);
      return undefined;
    }

    $$tracker.clickSharePoster('copy_link', this.trackerPosterKind, this.trackerRequestId);

    this.isCopying = true;
    let isSupport = false;
    try {
      // 关闭 copy 默认成功 Toast / 失败 Modal（component-recall A3 决策）：文案与领奖顺序由页面 Store 统一编排
      ({ isSupport } = copy(this.currentCopyTarget, {
        autoDisplaySupportedAlert: false,
        autoDisplayUnsupportedAlert: false
      }));
    } finally {
      // copy 为同步 API 且可能抛异常：异常路径同样必须解锁，否则复制按钮永久锁死（成功/失败/Toast 语义不变）
      this.isCopying = false;
    }

    if (isSupport) {
      Toast.success(COPY_SUCCESS_MESSAGE);
    } else {
      Toast.fail(COPY_FAILED_MESSAGE);
    }

    return undefined;
  }

  /**
   * 「保存海报」（底部海报按钮）：以当前门派/性别视觉、身份 ViewModel 与当前 QR 目标组织
   * CanvasImagePainter 绘制成员（成员布局知识经 mo-poster 的 createPosterMembers 导出），
   * 复用 TakePhoto.append 命令式弹层输出可长按保存的图片；弹窗底部经 footer 槽挂载
   * 「复制分享链接」按钮（2026-09-20 需求，复制编排复用 copyCurrentTarget）。
   * 前置阻断通过即视为分享意图，立即异步申请首分享奖励（首次判定与幂等由后端保证，
   * 不与海报绘制成败耦合，领奖失败只 Toast 不阻塞主操作；弹窗内复制不再重复申请）。
   * 成功/失败边界 = CanvasImagePainter 的 canvasDidDrawSuccess / canvasDidDrawError 回调
   * （TakePhoto 渲染时以 restProps 透传给内部 CanvasImagePainter，现有封装即满足透传，无需扩展共享组件）；
   * CanvasImagePainter 在任何父级重渲染（含关闭弹窗的 visible setState）时会整幅重画并重复回调，
   * 故回调以一次性闩锁结算——一次保存只 Toast 一次并解锁一次。
   * 成功 Toast「海报已保存」并停留当前编辑页（交互-6-3，不切结果页）；失败 Toast、
   * 解除操作锁允许重试（边界-6-2）；进行中重复点击被锁忽略（边界-6-3 并发防护）。
   * 前置阻断顺序与 copyCurrentTarget 一致。
   *
   * @returns {undefined}
   */
  @autobind
  savePoster() {
    if (this.isSavingPoster) {
      return undefined;
    }

    if (this.loadStatus !== 'ready') {
      return undefined;
    }

    if (!this.isCurrentQrTargetAvailable) {
      Toast.fail(QR_TARGET_UNAVAILABLE_MESSAGE);
      return undefined;
    }

    $$tracker.clickSharePoster('save_poster', this.trackerPosterKind, this.trackerRequestId);
    // 阻断通过即视为分享意图：立即异步申请首分享奖励（首次判定与幂等由后端保证），不与绘制成败耦合
    this.claimSharePrizeAfterSuccess();

    this.isSavingPoster = true;
    // 重复回调来源：挂载绘制 + TakePhoto 700ms isCanvasVisible setState 重绘 + 关闭弹窗 visible setState 重绘；
    // 成功/失败共用一个闩锁，一次保存只结算一次反馈与解锁
    let settled = false;
    const settleOnce = settle => () => {
      if (settled) {
        return undefined;
      }

      settled = true;
      settle();
      return undefined;
    };

    TakePhoto.append({
      width: POSTER_CANVAS_SIZE.width,
      height: POSTER_CANVAS_SIZE.height,
      toDataURLType: 'Png',
      members: createPosterMembers({
        sectKey: this.sectKey,
        viewModel: this.posterViewModel,
        qrTarget: this.currentQrTarget,
        // 说明文案图随 qrMode 三态取用（2026-09-24 修复：home/download 态保存图文案与码矛盾）
        qrMode: this.qrMode
      }),
      // 分享弹窗底部「复制分享链接」按钮（2026-09-20 需求）：复制编排复用 copyCurrentTarget，
      // 首分享奖励已随本次点击申请，弹窗内复制不再重复调用 claim_prize_share
      footer: <ShareCopyFooter onClick={this.copyCurrentTarget} />,
      warnTip: '长按/截图可保存分享，或复制链接发送至亲友',
      canvasDidDrawSuccess: settleOnce(() => {
        this.isSavingPoster = false;
        // 结束态（download）不进入领奖链路（交互-6-5），保持不带引导的原文案；按态判定与 claimSharePrizeAfterSuccess 跳过口径同源
        Toast.success(this.qrMode === 'download' ? POSTER_SAVED_MESSAGE : POSTER_SAVED_WITH_PRIZE_GUIDE_MESSAGE);
      }),
      canvasDidDrawError: settleOnce(() => {
        this.isSavingPoster = false;
        Toast.fail(POSTER_GENERATE_FAILED_MESSAGE);
      })
    });

    return undefined;
  }

  /**
   * 首分享奖励申请（交互-6-4）：保存海报（打开分享弹窗）的前置阻断通过（分享意图确认）后触发，
   * 不与主操作成败耦合；2026-09-20 需求起复制入口移入分享弹窗底部，弹窗内复制不再重复申请
   * （领奖已随打开弹窗发出）。
   * 活动中按当前 qrMode 透传 qrcode_type（服务首参 _mock_passport_account 为测试环境通行证占位，生产恒 undefined）；
   * 结束态（download）直接跳过调用（交互-6-5）。
   * 失败只 Toast 不回滚、不阻断后续操作；前端不维护「已领奖」状态，
   * 重复触发由后端幂等保证（边界-6-3）。
   *
   * @returns {Promise<undefined>} 奖励链路落地后 resolve；失败被吞并 Toast，永不 reject。
   */
  @autobind
  claimSharePrizeAfterSuccess() {
    if (this.qrMode === 'download') {
      return Promise.resolve(undefined);
    }

    return Promise.resolve()
      .then(() => this.$ztService.claimSharePrize(undefined, this.qrMode))
      .catch(error => {
        Toast.fail(getErrorMessage(error, SHARE_PRIZE_FAILED_MESSAGE));
        return undefined;
      });
  }

  /**
   * 「右上角关闭」入口（Task 7 来源感知关闭）：返回显式传入的合法来源页，
   * 来源缺失或非法时统一回组队主页（findings 用户确认-1）。
   * MAIN/TEAM 复用 BaseStore.redirect；GUEST 只把 use() 快照的三项受控参数交给 formatRoute 编码；
   * VETERAN_REVIEW 带 view=summary 精准回流回顾总结视图（query 为页面常量，不透传用户输入，白名单安全模型不变）。
   *
   * @returns {undefined}
   */
  @autobind
  closePage() {
    if (this.closeSourceRouteKey === 'GUEST') {
      this.$history.push(formatRoute(routeConfigs.GUEST.path, this.guestCloseQueryParams));
      return undefined;
    }

    if (this.closeSourceRouteKey === 'VETERAN_REVIEW') {
      this.$history.push(formatRoute(routeConfigs.VETERAN_REVIEW.path, { view: 'summary' }));
      return undefined;
    }

    this.redirect(this.closeSourceRouteKey || CLOSE_FALLBACK_ROUTE_KEY);
    return undefined;
  }

  /**
   * 标签候选池 getter（构造注入给标签弹窗 Store）。
   *
   * @returns {string[]} 候选池纯数组副本。
   */
  @autobind
  getCandidateTagList() {
    return this.tagList.slice();
  }

  /**
   * 当前标签确认值 getter（构造注入给标签弹窗 Store）。
   *
   * @returns {string[]} 确认标签纯数组副本。
   */
  @autobind
  getConfirmedTagList() {
    return this.confirmedTags.slice();
  }

  /**
   * 标签达成条件描述 getter（构造注入给标签弹窗 Store，交互-6-10 描述区数据源）：
   * 读 tagDescMap（2026-09-21 新增 desc 字段的展示链路），缺失兜底空串由弹窗按无描述渲染。
   *
   * @param {string} tag 标签文本。
   * @returns {string} 达成条件描述；无数据时为空串。
   */
  @autobind
  getTagDesc(tag) {
    const desc = this.tagDescMap[tag];
    return typeof desc === 'string' ? desc : '';
  }

  /**
   * 标签稀有度 getter（注入标签弹窗）：映射缺失（旧数组形态/未知标签）兜底稀有度 1。
   *
   * @param {string} tag 标签文本。
   * @returns {number} 稀有度 1/2/3。
   */
  @autobind
  getTagRarity(tag) {
    const rarity = this.tagRarityMap[tag];
    return rarity === 2 || rarity === 3 ? rarity : 1;
  }

  /**
   * 当前门派确认值 getter（构造注入给门派弹窗 Store）。
   *
   * @returns {string} 门派键。
   */
  @autobind
  getCurrentSect() {
    return this.confirmedSect;
  }

  /**
   * 当前性别确认值 getter（构造注入给门派弹窗 Store）。
   *
   * @returns {string} 性别键。
   */
  @autobind
  getCurrentGender() {
    return this.confirmedGender;
  }
}

export default EndedPosterStore;
